import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core'
import { Subscription } from 'rxjs'
import { Router } from '@angular/router'
import { Store } from '@ngrx/store'
import {
  HaulexGeneralNotification,
  HaulexGeneralNotificationLevel,
  HaulexGeneralNotificationService
} from '@app/core/services/haulex-general-notification.service'
import { AuthService } from '@app/users/services'
import { FOLLOW_UP_RESOURCE } from '@app/shared/models/followUpTypes.modal'
import { OpenModal } from '@app/modals/store'
import { RedirectService } from '@app/shared'
import * as base64Util from '@app/shared/utils/base64.util'

@Component({
  selector: 'app-haulex-general-notifications',
  template: `
    @if (notifications.length) {
      <div class="hx-gn__container">
        @for (n of notifications; track n._id) {
          <div class="hx-gn__card hx-gn__card--{{ n.level || 'info' }}">
            <!-- header row: HaulEx if global, company name if company-scoped, + dismiss -->
            <div class="hx-gn__head">
              <span class="hx-gn__head-title">{{ headTitle(n) }}</span>
              <button type="button" class="hx-gn__close" (click)="dismiss(n)" aria-label="Dismiss">
                <i class="fa-solid fa-xmark"></i>
              </button>
            </div>
            <!-- title row: severity icon + title + optional open-resource link -->
            <div class="hx-gn__title-row">
              <span class="hx-gn__icon"><i class="{{ iconClass(n.level) }}"></i></span>
              <span class="hx-gn__title">{{ n.title }}</span>
              @if (n.resourceId) {
                <a class="hx-gn__link" title="Open Resource" (click)="redirect(n)">
                  <i class="fa-solid fa-arrow-up-right-from-square"></i>
                </a>
              }
            </div>
            @if (n.description) {
              <div class="hx-gn__desc">{{ n.description }}</div>
            }
          </div>
        }
      </div>
    }
  `,
  styles: [
    `
      .hx-gn__container {
        position: fixed;
        bottom: 16px;
        right: 16px;
        z-index: 9999;
        display: flex;
        flex-direction: column;
        gap: 10px;
        width: 360px;
        max-width: calc(100vw - 32px);
        pointer-events: none;
      }
      .hx-gn__card {
        pointer-events: auto;
        display: flex;
        flex-direction: column;
        gap: 8px;
        padding: 12px;
        border-radius: 8px;
        border-left: 4px solid #9ca3af;
        background: #fff;
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.14);
        overflow: hidden;
        animation: hx-gn-in 0.25s ease-out;
      }
      /* severity tints: left stripe = feedback text token, card fill = feedback bg token */
      .hx-gn__card--error {
        border-left-color: #9b1c1c;
        background: #fde8e8;
      }
      .hx-gn__card--warning {
        border-left-color: #725b13;
        background: #fdf6b2;
      }
      .hx-gn__card--info {
        border-left-color: #1e429f;
        background: #e1effe;
      }
      .hx-gn__head {
        display: flex;
        align-items: center;
        gap: 8px;
      }
      .hx-gn__head-title {
        flex: 1 1 0;
        min-width: 0;
        font-size: 12px;
        font-weight: 600;
        line-height: 16px;
        color: #111928;
        word-break: break-word;
      }
      .hx-gn__close {
        flex-shrink: 0;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 24px;
        background: transparent;
        border: 0;
        cursor: pointer;
        color: #6b7280;
        font-size: 14px;
        line-height: 1;
        padding: 0;
      }
      .hx-gn__close:hover {
        color: #111928;
      }
      .hx-gn__title-row {
        display: flex;
        align-items: center;
        gap: 6px;
      }
      .hx-gn__icon {
        flex-shrink: 0;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        height: 16px;

        i {
          font-size: 13px;
        }
      }
      .hx-gn__card--error .hx-gn__icon {
        color: #b91c1c;
      }
      .hx-gn__card--warning .hx-gn__icon {
        color: #725b13;
      }
      .hx-gn__card--info .hx-gn__icon {
        color: #1e429f;
      }
      .hx-gn__title {
        flex: 1 1 0;
        min-width: 0;
        font-size: 14px;
        font-weight: 600;
        line-height: 20px;
        color: #111928;
        word-break: break-word;
      }
      .hx-gn__link {
        flex-shrink: 0;
        cursor: pointer;
        line-height: 1;
        padding-right: 6px;
      }
      .hx-gn__link i {
        color: #1d4ed8 !important;
        font-size: 12px;
      }
      .hx-gn__link:hover i {
        color: #1e429f !important;
      }
      .hx-gn__desc {
        font-size: 14px;
        font-weight: 400;
        line-height: 24px;
        color: #6b7280;
        word-break: break-word;
      }
      @keyframes hx-gn-in {
        from {
          opacity: 0;
          transform: translateY(8px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
    `
  ],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HaulexGeneralNotificationListComponent implements OnInit, OnDestroy {
  notifications: HaulexGeneralNotification[] = []
  private sub: Subscription

  constructor(
    private notificationService: HaulexGeneralNotificationService,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private store: Store<any>,
    private redirectService: RedirectService
  ) {}

  ngOnInit() {
    this.sub = this.notificationService.notifications$.subscribe(list => {
      this.notifications = this.filterVisible(list || [])
      this.cdr.detectChanges()
    })
  }

  ngOnDestroy() {
    this.sub?.unsubscribe()
  }

  // Show only valid notifications (per-user dismissals are handled server-side via ignoredBy, so a
  // dismissed one simply isn't returned by the API):
  // - info/warning while their endDate is still in the future
  // - error while it is not resolved
  private filterVisible(list: HaulexGeneralNotification[]): HaulexGeneralNotification[] {
    const now = Date.now()
    return list.filter(n => {
      if (n.level === 'error') return !n.resolved
      // info / warning
      return !!n.endDate && new Date(n.endDate).getTime() > now
    })
  }

  headTitle(n: HaulexGeneralNotification): string {
    if (n.general) return 'HaulEx'
    return AuthService.CURRENT_USER?.company?.name || 'Company'
  }

  iconClass(level?: HaulexGeneralNotificationLevel): string {
    switch (level) {
      case 'error':
      case 'warning':
        return 'fa-solid fa-triangle-exclamation'
      case 'info':
      default:
        return 'fa-solid fa-circle-info'
    }
  }

  // Open the linked resource — mirrors the scheduled-events (HeaderEventsComponent) redirect logic
  redirect(n: HaulexGeneralNotification) {
    const resource = n.resource
    const resourceId = n.resourceId
    if (!resource || !resourceId) return
    const ev: any = n
    const extraResourceId = ev.extraResourceId
    const queryParams = ev.params ? { queryParams: ev.params } : {}
    const isLoadboard = window.location.href.indexOf('loadboard') > -1
    const userIndex = this.redirectService.getUrlIndex()
    const currentUser = AuthService.CURRENT_USER
    switch (resource) {
      case FOLLOW_UP_RESOURCE.CLAIM:
        return this.router.navigate([`/${userIndex}/claims/${resourceId}`])
      case FOLLOW_UP_RESOURCE.ORDER:
      case FOLLOW_UP_RESOURCE.ORDER_BILLING:
      case FOLLOW_UP_RESOURCE.ORDER_BROKER:
        return this.router.navigate(isLoadboard ? [`/${userIndex}/loadboard/${resourceId}`] : [`/${userIndex}/quotes/${resourceId}`])
      case FOLLOW_UP_RESOURCE.ORDER_COLLECTION:
      case FOLLOW_UP_RESOURCE.ORDER_FILTERS:
        return this.router.navigate(isLoadboard ? [`/${userIndex}/loadboard`] : [`/${userIndex}/quotes`], queryParams)
      case FOLLOW_UP_RESOURCE.ACCOUNT_CONTACT:
        return this.router.navigate([`/${userIndex}/crm/accounts/${resourceId}`])
      case FOLLOW_UP_RESOURCE.CONTACT_NOTE:
        return this.store.dispatch(
          new OpenModal({ type: 'UPDATE_CONTACT', props: { contactId: resourceId, size: 'md', windowClass: 'contact-modal' } })
        )
      case FOLLOW_UP_RESOURCE.EQUIPMENT:
        return this.store.dispatch(
          new OpenModal({
            type: 'EQUIPMENT_FORM',
            props: { _id: resourceId, carrierId: currentUser?.company?._id, tab: 'Notes', windowClass: 'equipment-form-modal' }
          })
        )
      case FOLLOW_UP_RESOURCE.ORDER_ACCOUNTING:
        return this.router.navigate(isLoadboard ? [`/${userIndex}/loadboard/${resourceId}/accounting`] : [`/${userIndex}/quotes/${resourceId}/accounting`])
      case FOLLOW_UP_RESOURCE.SHOP_ORDER:
        return this.router.navigate([`/${userIndex}/shop/${resourceId}`])
      case FOLLOW_UP_RESOURCE.SHOP_SERVICE:
        this.router.navigate([`/${userIndex}/shop/${ev._orderId}`])
        return this.store.dispatch(new OpenModal({ type: 'SHOP_SERVICE', props: { _id: resourceId, _orderId: ev._orderId } }))
      case FOLLOW_UP_RESOURCE.CANDIDATE:
        return this.router.navigate([`/${userIndex}/recruiter/${resourceId}`])
      case FOLLOW_UP_RESOURCE.EXPENSE:
        return this.store.dispatch(new OpenModal({ type: 'EXPENSE', props: { _id: resourceId } }))
      case FOLLOW_UP_RESOURCE.TRIP:
        return this.router.navigate([`/${userIndex}/trips/${resourceId}/stops`])
      case FOLLOW_UP_RESOURCE.BOOK_NOW: {
        const hash =
          ev._carrierId === currentUser?.company?._id
            ? base64Util.encodeUrlSafe(JSON.stringify({ orderId: resourceId, carrierId: ev._carrierId }))
            : base64Util.encodeUrlSafe(JSON.stringify({ orderId: resourceId }))
        return this.router.navigate([`/${userIndex}/v/loadboard/orders/${hash}`])
      }
      case FOLLOW_UP_RESOURCE.INVOICE:
        return this.router.navigate([`/${userIndex}/${isLoadboard ? 'loadboard' : 'quotes'}/${extraResourceId}/accounting`])
      case FOLLOW_UP_RESOURCE.COMPANY:
        return this.router.navigate([`/${userIndex}/crm/accounts/${resourceId}`])
      default:
        return this.router.navigate([`/${userIndex}/${resource}/${resourceId}`])
    }
  }

  dismiss(n: HaulexGeneralNotification) {
    // Persist a per-user dismissal server-side (ignoredBy) so it stays hidden across sessions and
    // devices — the API won't return it again for this user.
    if (n?._id) {
      this.notificationService.ignore(n._id).subscribe({ error: () => {} })
    }
    this.notifications = this.notifications.filter(x => x !== n)
    this.cdr.detectChanges()
  }
}
